/**
 * Audit Detail Data Hook
 *
 * Wraps the getAuditDetail Convex query and computes dashboard metrics
 * from the returned categories and documents data. This is the single
 * data source for the entire audit detail page.
 */

import { useQuery as useConvexQuery } from "convex/react";
import { api } from "convex/_generated/api";
import { useMemo } from "react";
import type { Id } from "@/packages/types";
import { useWorkspaceQuery } from "@/hooks";
import type {
  UIAudit,
  AuditDashboardMetrics,
  AuditChecklistStats,
  AuditDocumentStats,
  AuditFindingStats,
  UIAuditCategory,
  AuditActivity,
  Company,
  AuditDocument,
} from "@/packages/types";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/**
 * Compute days remaining until a deadline timestamp.
 * Returns 0 if the deadline has passed.
 */
export function computeDaysRemaining(deadline: number): number {
  const diff = deadline - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

/**
 * Compute checklist stats from categories by counting item statuses.
 * APPROVED = completed, UPLOADED = inProgress, MISSING = notStarted
 */
function computeChecklistStats(
  categories: UIAuditCategory[],
): AuditChecklistStats {
  let completed = 0;
  let inProgress = 0;
  let notStarted = 0;

  for (const category of categories) {
    for (const item of category.items) {
      switch (item.status) {
        case "APPROVED":
          completed++;
          break;
        case "UPLOADED":
          inProgress++;
          break;
        case "MISSING":
          notStarted++;
          break;
      }
    }
  }

  const total = completed + inProgress + notStarted;
  return { total, completed, inProgress, notStarted };
}

/**
 * Compute document stats from audit documents.
 * Since we don't have review/flagged status on documents yet,
 * we derive simple stats from available data.
 */
function computeDocumentStats(documents: AuditDocument[]): AuditDocumentStats {
  const total = documents.length;
  // For now, all documents with a linked checklist item are considered "reviewed"
  const reviewed = documents.filter((d) => d.linkedChecklistItemId).length;
  const pending = total - reviewed;
  const flagged = 0; // Not implemented yet

  return { total, reviewed, pending, flagged };
}

/**
 * Compute dashboard metrics from audit data.
 */
/** Map schema severity to dashboard severity bucket */
const severityMap: Record<
  string,
  keyof Pick<AuditFindingStats, "critical" | "high" | "medium" | "low">
> = {
  critical: "critical",
  major: "high",
  minor: "medium",
  observation: "low",
};

function computeFindingStats(
  findings: Array<{ severity: string; status: string }> | undefined,
): AuditFindingStats {
  const stats: AuditFindingStats = {
    total: 0,
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    resolved: 0,
  };
  if (!findings) return stats;
  for (const f of findings) {
    stats.total++;
    const bucket = severityMap[f.severity];
    if (bucket) stats[bucket]++;
    if (f.status === "resolved") stats.resolved++;
  }
  return stats;
}

function computeDashboardMetrics(
  audit: UIAudit,
  categories: UIAuditCategory[],
  documents: AuditDocument[],
  findings: Array<{ severity: string; status: string }> | undefined,
): AuditDashboardMetrics {
  const checklistStats = computeChecklistStats(categories);
  const documentStats = computeDocumentStats(documents);
  const findingStats = computeFindingStats(findings);

  const completionPercentage =
    audit.progress.total > 0
      ? Math.round((audit.progress.completed / audit.progress.total) * 100)
      : 0;

  // Risk score: derive from AI risks if available, otherwise 0
  const riskCount = audit.aiSummaryData?.risks?.length ?? 0;
  const riskScore = Math.min(riskCount * 20, 100);

  return {
    completionPercentage,
    riskScore,
    checklistStats,
    documentStats,
    findingStats,
  };
}

/* ------------------------------------------------------------------ */
/* Hook                                                                */
/* ------------------------------------------------------------------ */

export interface AuditFinding {
  _id: string;
  title: string;
  severity: "critical" | "major" | "minor" | "observation";
  status: "draft" | "open" | "resolved";
}

interface UseAuditDetailReturn {
  audit: UIAudit | undefined;
  company: Company | undefined;
  categories: UIAuditCategory[] | undefined;
  documents: AuditDocument[] | undefined;
  activities: AuditActivity[] | undefined;
  findings: AuditFinding[] | undefined;
  dashboardMetrics: AuditDashboardMetrics | undefined;
  daysRemaining: number | undefined;
  isLoading: boolean;
}

export function useAuditDetail(
  auditId: Id<"audits"> | null | undefined,
): UseAuditDetailReturn {
  const { data, isLoading } = useWorkspaceQuery(
    api.audit_queries.getAuditDetail,
    { auditId: auditId ?? null },
  );

  const findings = useConvexQuery(
    api.finding_queries.getByAudit,
    auditId ? { auditId } : "skip",
  );

  const result = useMemo(() => {
    if (!data) {
      return {
        audit: undefined,
        company: undefined,
        categories: undefined,
        documents: undefined,
        activities: undefined,
        findings: undefined,
        dashboardMetrics: undefined,
        daysRemaining: undefined,
      };
    }

    const { audit, company, categories, documents, activities } = data;

    const dashboardMetrics = computeDashboardMetrics(
      audit,
      categories,
      documents,
      findings ?? undefined,
    );

    const daysRemaining = computeDaysRemaining(audit.deadline);

    return {
      audit,
      company,
      categories,
      documents,
      activities,
      findings: findings ?? undefined,
      dashboardMetrics,
      daysRemaining,
    };
  }, [data, findings]);

  return {
    ...result,
    isLoading,
  };
}
